import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { PlusIcon, MapPinIcon, Bars3Icon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Plus } from 'lucide-react';
import { projectAPI, thingsAPI } from '../services/api';
import ProjectCard from '../components/ProjectCard';
import ObservationCard from '../components/ObservationCard';
import ListMapView from '../components/ListMapView';
import ListPageContent from '../components/ListPageContent';
import MobileSearchOverlay from '../components/MobileSearchOverlay';
import SearchUI from '../components/SearchUI';
import { useListMapView } from '../hooks/useListMapView';
import { useListPageData } from '../hooks/useListPageData';
import { useListPageFilters } from '../hooks/useListPageFilters';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../config/runtime';
import tagsData from '../data/tags.json';
import { getLocationFilterParams } from '../utils/locationFilters';
import { projectHasGeolocation } from '../utils/projectLocation';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'history', label: 'Archived' },
  { value: 'observations', label: 'My Observations' },
];

const mapStatusFilterToQuery = (statusValue) => {
  if (!statusValue) return null;
  if (statusValue === 'history') {
    return 'finished';
  }
  return statusValue;
};

const ProjectsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Use the reusable list/map view hook
  const listMapState = useListMapView({
    enableMap: true,
    treatTabletAsMobile: true,
    autoEnableMapFilter: true,
  });

  const {
    isMobile,
    viewMode,
    shouldRenderMobileMap,
    isDesktopSplitView,
    userLocation,
    isMobileSearchOpen,
    setIsMobileSearchOpen,
    mobileSearchHeaderRef,
    handleDesktopViewChange,
    isFullyExpanded,
  } = listMapState;

  // Combined filter tags
  const allFilterTags = useMemo(() => [
    { id: 'nearby', label: 'Nearby', icon: '📍', description: 'Find what\'s around you' },
    ...tagsData.tags
  ], []);

  // Use filter hook with search and status filter enabled
  const filters = useListPageFilters({
    enableSearch: true,
    enableTags: true,
    enableSort: false,
    enableStatus: true,
    defaultStatus: 'active',
    statusOptions: STATUS_OPTIONS,
  });

  const {
    searchTerm,
    mobileSearchTerm,
    selectedTags,
    statusFilter,
    mapViewportCenter,
    mapUserInteracted,
    mapSyncEnabled,
    setSearchTerm,
    setMobileSearchTerm,
    setSelectedTags,
    setStatusFilter,
    setMapViewportBbox,
    setMapViewportCenter,
    setMapViewportRadius,
    setMapUserInteracted,
    setMapSyncEnabled,
    handleSearch,
    clearAllFilters,
    filters: filterValues,
  } = filters;

  const mapAreaFilterActive = Boolean(
    mapViewportCenter && mapUserInteracted && mapSyncEnabled
  );

  // Debug: Log filter values changes
  useEffect(() => {
    console.log('[ProjectsPage] filterValues changed', {
      mapViewportCenter: filterValues.mapViewportCenter,
      mapViewportRadius: filterValues.mapViewportRadius,
      mapUserInteracted: filterValues.mapUserInteracted,
      mapSyncEnabled: filterValues.mapSyncEnabled,
      fullFilterValues: filterValues,
    });
  }, [filterValues]);

  const viewportStateHandlersRef = useRef({
    handleClearMapAreaFilter: null,
    handleEnableMapAreaFilter: null,
  });

  const handleViewportStateChange = useCallback((viewportState) => {
    const isMapVisible = shouldRenderMobileMap || (!isMobile && viewMode === 'map');

    viewportStateHandlersRef.current = {
      handleClearMapAreaFilter: viewportState.handleClearMapAreaFilter,
      handleEnableMapAreaFilter: viewportState.handleEnableMapAreaFilter,
    };

    setMapSyncEnabled(viewportState.mapSyncEnabled);
    setMapUserInteracted(viewportState.mapUserInteracted);

    if (isMapVisible && viewportState.mapViewportCenter) {
      setMapViewportBbox(viewportState.mapViewportBbox);
      setMapViewportCenter(viewportState.mapViewportCenter);
      setMapViewportRadius(viewportState.mapViewportRadius);
    }
  }, [shouldRenderMobileMap, isMobile, viewMode, setMapViewportBbox, setMapViewportCenter, setMapViewportRadius, setMapUserInteracted, setMapSyncEnabled]);

  const handleClearMapAreaFilter = useCallback(() => {
    viewportStateHandlersRef.current.handleClearMapAreaFilter?.();
  }, []);



  // Build query parameters
  const buildQueryParams = useCallback((filters, pageParam, limit) => {
    const params = {
      offset: pageParam,
      limit,
    };

    // Add status filter - backend now supports 'active' (ongoing + pending), 'history' (finished), or specific status
    const statusQueryValue = mapStatusFilterToQuery(filters.statusFilter);
    if (statusQueryValue) {
      params.status = statusQueryValue;
    }

    // Add search term
    if (filters.searchTerm) {
      params.search = filters.searchTerm;
    }

    // Add tags (excluding 'nearby' which is handled separately)
    const allTags = (filters.selectedTags || []).filter(tag => tag !== 'nearby');
    if (allTags.length > 0) {
      params.tags = allTags.join(',');
    }

    const locationParams = getLocationFilterParams(filters, userLocation);

    if (locationParams) {
      params.lat = locationParams.lat;
      params.lng = locationParams.lng;
      params.radius = locationParams.radius;
      console.log('[ProjectsPage] buildQueryParams: Using location filter', {
        ...params,
        source: locationParams.source,
      });
    } else {
      console.log('[ProjectsPage] buildQueryParams: No location filter', {
        mapViewportCenter: filters.mapViewportCenter,
        mapUserInteracted: filters.mapUserInteracted,
        mapSyncEnabled: filters.mapSyncEnabled,
        hasNearbyTag: (filters.selectedTags || []).includes('nearby'),
        userLocation,
      });
    }

    return params;
  }, [userLocation]);

  // Transform response
  const transformResponse = useCallback((response) => {
    return response?.data || [];
  }, []);


  // Check if in observations mode
  const isObservationsMode = statusFilter === 'observations';
  
  // Use data hook for projects
  const projectsData = useListPageData({
    queryKey: 'user-projects',
    queryFn: (params) => projectAPI.getAll(params),
    buildParams: buildQueryParams,
    transformResponse,
    filters: filterValues,
    limit: 20,
    maxPages: 5,
    staleTime: 0,
    queryOptions: {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    enabled: !isObservationsMode, // Only fetch when not in observations mode
  });

  // Observations query - fetch user's observations from ThingsDB
  const observationsQuery = useInfiniteQuery({
    queryKey: ['my-observations', filterValues.searchTerm, user?.id],
    queryFn: async ({ pageParam = 1 }) => {
      const params = {
        page: pageParam,
        limit: 50,
        layer: 'detections',
        user_id: user?.id, // Filter by current user
      };
      if (filterValues.searchTerm) {
        params.search = filterValues.searchTerm;
      }
      const response = await thingsAPI.getItems(params);
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const items = lastPage?.things || [];
      if (items.length === 0 || items.length < 50) return undefined;
      if (allPages.length >= 10) return undefined;
      return allPages.length + 1;
    },
    staleTime: 5 * 60 * 1000,
    enabled: isObservationsMode && !!user?.id,
  });

  const observations = useMemo(() => {
    return observationsQuery.data?.pages.reduce((acc, page) => {
      return [...acc, ...(page?.things || [])];
    }, []) || [];
  }, [observationsQuery.data]);

  // Unified data access - switch between projects and observations
  const {
    projects,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = useMemo(() => {
    if (isObservationsMode) {
      return {
        projects: observations,
        isLoading: observationsQuery.isLoading,
        isFetchingNextPage: observationsQuery.isFetchingNextPage,
        hasNextPage: observationsQuery.hasNextPage,
        fetchNextPage: observationsQuery.fetchNextPage,
        refetch: observationsQuery.refetch,
        error: observationsQuery.error,
      };
    }
    return {
      projects: projectsData.projects,
      isLoading: projectsData.isLoading,
      isFetchingNextPage: projectsData.isFetchingNextPage,
      hasNextPage: projectsData.hasNextPage,
      fetchNextPage: projectsData.fetchNextPage,
      refetch: projectsData.refetch,
      error: projectsData.error,
    };
  }, [isObservationsMode, observations, observationsQuery, projectsData]);

  const projectsForDisplay = useMemo(() => {
    if (isObservationsMode) {
      // For observations, filter by geolocation if map area filter is active
      if (mapAreaFilterActive) {
        return projects.filter(item => Number.isFinite(item.lat) && Number.isFinite(item.lng));
      }
      return projects;
    }
    
    // Filter by geolocation if map area filter is active
    const filtered = mapAreaFilterActive
      ? projects.filter(projectHasGeolocation)
      : projects;
    
    // Sort so "live" (ongoing) projects appear first, then maintain backend date ordering
    return [...filtered].sort((a, b) => {
      const aIsLive = a.status === 'ongoing';
      const bIsLive = b.status === 'ongoing';
      
      // Live projects come first
      if (aIsLive && !bIsLive) return -1;
      if (!aIsLive && bIsLive) return 1;
      
      // Within the same status group, maintain original order (backend date ordering)
      return 0;
    });
  }, [projects, mapAreaFilterActive, isObservationsMode]);

  // Listen for project mutations and invalidate queries
  useEffect(() => {
    const unsubscribe = queryClient.getMutationCache().subscribe((mutation) => {
      if (mutation?.status === 'success') {
        const mutationKey = mutation?.options?.mutationKey;
        const mutationFn = mutation?.options?.mutationFn;
        
        const isProjectMutation = 
          (Array.isArray(mutationKey) && 
           (mutationKey[0] === 'update-project' || 
            mutationKey[0] === 'delete-project' ||
            mutationKey[0] === 'create-project' ||
            mutationKey[0] === 'update-status')) ||
          (typeof mutationFn === 'function' && 
           (mutationFn.toString().includes('projects') || 
            mutationFn.toString().includes('projectAPI')));
        
        if (isProjectMutation) {
          queryClient.invalidateQueries({ queryKey: ['user-projects'] });
        }
      }
    });

    return unsubscribe;
  }, [queryClient]);

  // Listen for focus/visibility to refetch when user returns
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: ['user-projects'] });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [queryClient]);

  // Refetch when navigating to this page
  useEffect(() => {
    if (location.pathname === '/projects') {
      refetch();
    }
  }, [location.pathname, refetch]);

  // Apply mobile search
  const applyMobileSearch = useCallback(() => {
    handleSearch();
    setIsMobileSearchOpen(false);
  }, [handleSearch, setIsMobileSearchOpen]);

  // Handle tag toggle
  const handleTagToggle = useCallback((tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(t => t !== tagId));
    } else {
      setSelectedTags([tagId]);
    }
  }, [selectedTags, setSelectedTags]);

  // Handle nearby toggle
  const handleNearbyToggle = useCallback(() => {
    setSelectedTags((prev) => {
      if (prev.includes('nearby')) {
        return prev.filter((tag) => tag !== 'nearby');
      }
      return [...prev, 'nearby'];
    });
  }, [setSelectedTags]);

  // Filter summary for mobile
  const mobileSearchSummary = useMemo(() => {
    if (searchTerm?.trim()) return searchTerm;
    if (isObservationsMode) {
      if (mapAreaFilterActive) return 'Observations in map area';
      return 'My Observations';
    }
    if (mapAreaFilterActive) return 'Projects in map area';
    return 'All Projects';
  }, [searchTerm, mapAreaFilterActive, isObservationsMode]);

  const selectedTagLabels = selectedTags
    .filter((tag) => tag !== 'nearby')
    .map((tag) => allFilterTags.find((t) => t.id === tag)?.label)
    .filter(Boolean);
  const mobileFilterSummary = useMemo(() => {
    const parts = [];
    if (selectedTagLabels.length > 0) {
      const preview = selectedTagLabels.slice(0, 2).join(', ');
      const extra = selectedTagLabels.length > 2 ? ` +${selectedTagLabels.length - 2}` : '';
      parts.push(`${preview}${extra}`);
    }
    if (statusFilter && statusFilter !== 'active') {
      parts.push(STATUS_OPTIONS.find(opt => opt.value === statusFilter)?.label || statusFilter);
    }
    return parts.length > 0 ? parts.join(' · ') : 'Any filters';
  }, [selectedTagLabels, statusFilter]);

  const handleOpenNewProjectPage = useCallback(() => {
    navigate('/new', {
      state: {
        previousPath: location.pathname,
        transitionDirection: 'forward',
      },
    });
  }, [navigate, location.pathname]);

  // Navigation handlers
  const navigateToProject = useCallback((projectId, source = 'unknown') => {
    navigate(`/projects/${projectId}`, {
      state: {
        previousPath: '/projects',
        transitionDirection: 'forward',
      },
    });
  }, [navigate]);

  const navigateToObservation = useCallback((observationId, source = 'unknown') => {
    navigate(`/discover/observation/${observationId}`, {
      state: {
        previousPath: '/projects',
        transitionDirection: 'forward',
        isObservation: true,
      },
    });
  }, [navigate]);

  // Render item (project or observation)
  const renderProject = useCallback((item, index) => {
    if (isObservationsMode) {
      return (
        <ObservationCard
          observation={item}
          onClick={(obs) => navigateToObservation(obs.id, 'card')}
        />
      );
    }
    return (
      <ProjectCard
        project={item}
        navigateTo="projects"
        preferCapturedCover={true}
        onProjectClick={(projectId) => navigateToProject(projectId, 'card')}
        isMobile={isMobile}
      />
    );
  }, [navigateToProject, navigateToObservation, isMobile, isObservationsMode]);

  // Render header with status filter
  const renderHeader = useCallback(() => {
    return (
      <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-between gap-4'}`}>
        <div>
          {!isMobile && (
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Projects</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Your active and completed projects</p>
            </div>
          )}
          {isMobile && isFullyExpanded && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-3">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">My Projects</h1>
                <div className="relative inline-block">
                  <select
                    id="status-filter-mobile"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      backgroundImage: 'none',
                      paddingRight: '1.75rem',
                    }}
                    className="text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                  >
                    <option value="active">Active</option>
                    <option value="history">Archived</option>
                    <option value="observations">My Observations</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-1.5 pointer-events-none">
                    <svg className="w-3 h-3 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Your active and completed projects</p>
            </div>
          )}
        </div>
        <div className={`flex ${isMobile ? 'flex-col gap-3 w-full' : 'items-center gap-3 flex-wrap justify-end'}`}>
          {!isMobile && (
            <>
              <div className="flex-1 max-w-md">
                <SearchUI
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  selectedTags={selectedTags}
                  setSelectedTags={setSelectedTags}
                  tags={allFilterTags}
                  searchPlaceholder="Search projects"
                  onSearch={handleSearch}
                  onClearAll={clearAllFilters}
                  className="w-full"
                  mapAreaFilterActive={mapAreaFilterActive}
                  onClearMapAreaFilter={handleClearMapAreaFilter}
                />
              </div>
              {/* Status Filter Dropdown */}
              <div className="flex items-center gap-2.5">
                <label htmlFor="status-filter" className="hidden sm:inline text-sm text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
                  Show:
                </label>
                <div className="relative inline-block w-auto">
                  <select
                    id="status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      backgroundImage: 'none',
                      paddingRight: '2rem',
                      minWidth: 'fit-content',
                    }}
                    className="text-sm font-medium text-gray-900 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-full px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer hover:border-gray-400 dark:hover:border-gray-500"
                  >
                    <option value="active">Active</option>
                    <option value="history">Archived</option>
                    <option value="observations">My Observations</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1 text-sm font-medium">
                <button
                  onClick={() => handleDesktopViewChange('list')}
                  className={`px-4 py-1.5 rounded-full transition-all flex items-center gap-1.5 ${
                    viewMode === 'list'
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <Bars3Icon className="w-4 h-4" />
                  List
                </button>
                <button
                  onClick={() => handleDesktopViewChange('map')}
                  className={`px-4 py-1.5 rounded-full transition-all flex items-center gap-1.5 ${
                    viewMode === 'map'
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <MapPinIcon className="w-4 h-4" />
                  Map
                </button>
              </div>
              
              <button
                onClick={handleOpenNewProjectPage}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-white px-5 py-1.5 font-semibold shadow-lg hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition"
              >
                <PlusIcon className="w-4 h-4" />
                New Project
              </button>
            </>
          )}
        </div>
      </div>
    );
  }, [
    isMobile,
    isFullyExpanded,
    statusFilter,
    viewMode,
    setStatusFilter,
    handleDesktopViewChange,
    searchTerm,
    selectedTags,
    allFilterTags,
    setSearchTerm,
    setSelectedTags,
    handleSearch,
    clearAllFilters,
    mapAreaFilterActive,
    handleClearMapAreaFilter,
    handleOpenNewProjectPage,
  ]);

  // Mobile search overlay using unified component
  const mobileSearchOverlay = (
    <MobileSearchOverlay
      isOpen={isMobile && isMobileSearchOpen}
      onClose={() => setIsMobileSearchOpen(false)}
      title="Search Projects"
      searchPlaceholder="Search Projects"
      mobileSearchTerm={mobileSearchTerm}
      onSearchTermChange={setMobileSearchTerm}
      onSearch={applyMobileSearch}
      selectedTags={selectedTags}
      onTagToggle={handleTagToggle}
      allTags={allFilterTags}
      showSort={false}
      showTags={true}
      mapAreaFilterActive={mapAreaFilterActive}
      onClearMapAreaFilter={handleClearMapAreaFilter}
      onClearAll={clearAllFilters}
      onNearbyToggle={handleNearbyToggle}
    />
  );

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async (invalidateKeys) => {
    if (isObservationsMode) {
      await queryClient.invalidateQueries({ queryKey: ['my-observations'] });
    } else {
      await queryClient.invalidateQueries({ queryKey: ['user-projects'] });
    }
    await refetch(invalidateKeys);
  }, [queryClient, refetch, isObservationsMode]);

  // Render list content using ListPageContent component (same as TemplatesPage)
  const listContent = (
    <ListPageContent
      projects={projectsForDisplay}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      refetch={refetch}
      error={error}
      renderProject={renderProject}
      isMobile={isMobile}
      isDesktopSplitView={isDesktopSplitView}
      isFullyExpanded={isFullyExpanded}
      onRefresh={handleRefresh}
      invalidateKeys={[['user-projects']]}
      emptyState={{
        title: isObservationsMode ? 'No observations yet' : 'No projects found',
        message: isObservationsMode
          ? 'Start exploring and making discoveries to see them here.'
          : statusFilter === 'history'
            ? 'No completed projects yet. Finish a project to see it here.'
            : 'Start your first project to see it here.',
        icon: isObservationsMode ? MagnifyingGlassIcon : Plus,
      }}
      header={renderHeader()}
      showLoadMoreButton={!isMobile}
    />
  );

  // Render map preview card
  const renderMapPreview = useCallback((item, { onClose }) => {
    // Handle observation preview
    if (isObservationsMode && (item._observation || item.canonical_name || item.label)) {
      const obs = item._observation || item;
      return (
        <div 
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
          onClick={() => {
            navigateToObservation(obs.id, 'map-preview-card');
            onClose();
          }}
        >
          <div className="relative">
            {obs.sample_images?.[0] ? (
              <img
                src={getImageUrl(obs.sample_images[0], 'cardmobile')}
                alt={obs.canonical_name || 'Discovery'}
                className="w-full h-44 object-cover"
              />
            ) : (
              <div className="w-full h-44 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-4xl">
                🔍
              </div>
            )}
          </div>
          
          <div className="p-3">
            <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-tight">
              {obs.canonical_name || obs.label || 'Unknown discovery'}
            </h3>
            
            {obs.project_name && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                in {obs.project_name}
              </p>
            )}
          </div>
        </div>
      );
    }

    // Default: Project preview
    const project = item;
    return (
      <div 
        className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
        onClick={() => {
          navigateToProject(project.id, 'map-preview-card');
          onClose();
        }}
      >
        <div className="relative">
          {project.images && project.images.length > 0 ? (
            <img
              src={getImageUrl(project.images[0], 'cardmobile')}
              alt={project.name}
              className="w-full h-44 object-cover"
            />
          ) : (
            <div className="w-full h-44 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <MapPinIcon className="w-12 h-12 text-gray-400" />
            </div>
          )}
        </div>
        
        <div className="p-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-tight">
              {project.name}
            </h3>
          </div>
          
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
            {project.description || 'No description'}
          </p>
          
          {project.distance && (
            <p className="text-sm text-gray-900 dark:text-white font-medium mt-2">
              {project.distance < 1 
                ? `${Math.round(project.distance * 1000)}m away`
                : `${project.distance.toFixed(1)}km away`
              }
            </p>
          )}
        </div>
      </div>
    );
  }, [navigateToProject, navigateToObservation, isObservationsMode]);

  // Render floating action button
  const renderFloatingActions = useCallback(() => {
    return (
      <button
        onClick={handleOpenNewProjectPage}
        className={`${isMobile ? 'w-16 h-16' : 'w-14 h-14'} bg-emerald-500 dark:bg-emerald-500 text-white rounded-full shadow-2xl flex items-center justify-center active:scale-95 transition-transform hover:bg-emerald-600`}
        style={{
          boxShadow: '0 10px 25px -5px rgba(6, 182, 212, 0.4), 0 8px 10px -6px rgba(6, 182, 212, 0.3)',
        }}
        aria-label="Start a new project"
      >
        <PlusIcon className={isMobile ? 'w-7 h-7' : 'w-6 h-6'} />
      </button>
    );
  }, [handleOpenNewProjectPage, isMobile]);

  return (
    <>
      {isMobile && !isMobileSearchOpen && (
        <div
          ref={mobileSearchHeaderRef}
          className="fixed pb-3 px-5 top-0 left-0 right-0 z-[700] pointer-events-auto bg-white dark:bg-gray-900"
          style={{ paddingTop: 'var(--app-safe-area-top)' }}
        >
          <button
            onClick={() => setIsMobileSearchOpen(true)}
            className="mt-3 w-full bg-white/95 dark:bg-gray-700 border border-gray-200 dark:border-gray-600/50 rounded-full px-4 h-[56px] shadow-lg flex items-center gap-3"
          >
            {!searchTerm?.trim() && selectedTags.length === 0 && statusFilter === 'active' && !mapAreaFilterActive ? (
              <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                <div className="flex items-center justify-center gap-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-black dark:text-gray-300" />
                  {isObservationsMode ? 'Search Observations' : 'Search Projects'}
                </div>
              </span>
            ) : (
              <div className="flex-1 text-center">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{mobileSearchSummary}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {mapAreaFilterActive
                    ? (mobileFilterSummary !== 'Any filters' ? `Map area · ${mobileFilterSummary}` : 'Filtered by map area')
                    : mobileFilterSummary}
                </p>
              </div>
            )}
          </button>
        </div>
      )}

      <ListMapView
        listContent={listContent}
        projects={isObservationsMode ? null : projectsForDisplay}
        mapPins={isObservationsMode ? projectsForDisplay
          .filter((d) => Number.isFinite(d.lat) && Number.isFinite(d.lng))
          .map((d) => ({
            id: d.id,
            lat: d.lat,
            lng: d.lng,
            name: d.canonical_name || d.label || 'Unknown',
            imageUrl: d.sample_images?.[0] || null,
            count: 1,
            type: d.type || d.category || 'detection',
            _observation: d,
          })) : []}
        onProjectSelect={isObservationsMode 
          ? (obsId) => navigateToObservation(obsId, 'mobile-map')
          : (projectId) => navigateToProject(projectId, 'mobile-map')
        }
        onMarkerSelect={(item) => {
          // Selected item is managed by ListMapView internally
        }}
        renderMapPreview={renderMapPreview}
        renderFloatingActions={renderFloatingActions}
        mapConfig={{
          mode: 'view',
          layoutContext: isObservationsMode ? 'discover' : 'projects',
          autoCenterOnUser: true,
        }}
        hookConfig={{
          enableMap: true,
          treatTabletAsMobile: true,
        }}
        hookState={listMapState}
        onViewportStateChange={handleViewportStateChange}
        emptyStateTitle={isObservationsMode ? 'No observations yet' : 'No projects found'}
        emptyStateMessage={isObservationsMode
          ? 'Start exploring and making discoveries to see them here.'
          : statusFilter === 'history'
            ? 'No completed projects yet. Finish a project to see it here.'
            : 'Start your first project to see it here.'}
      />

      {mobileSearchOverlay}
    </>
  );
};

export default ProjectsPage;
